import React, { useState, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Table, TableHeader, TableHead, TableRow, TableBody, TableCell } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from '@/hooks/use-toast';
import { formatCurrency } from '@/lib/utils';
import { PayoutOrder, PayoutStatus, RecipientType } from '@/models/Order';
import { payoutApi, PaginatedPayoutOrdersResponse } from '@/services/api/payoutApi';
import { associationApi } from '@/services/api/associationApi';
import { managementCompanyApi } from '@/services/api/organizationApi';
import { Building, Download, Home, Server, CreditCard, Loader2, ChevronLeft, ChevronRight, Check, X, Upload } from 'lucide-react';
import BackofficeLayout from '@/components/layouts/BackofficeLayout';
import { useAuth } from '@/contexts/AuthContext';
import { DateTime } from 'luxon';
import { extractDataArray } from '@/utils/apiUtils';

// Paid Payouts Table Component
const PaidPayoutsTable = ({ 
  payouts, 
  payoutTransactionDetails, 
  isLoadingTransactionDetails, 
  formatDate, 
  formatCurrency, 
  renderEntityIcon 
}: {
  payouts: any[];
  payoutTransactionDetails: any;
  isLoadingTransactionDetails: boolean;
  formatDate: (date: string) => string;
  formatCurrency: (amount: number) => string;
  renderEntityIcon: (type: string) => JSX.Element | null;
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const toggleRow = (payoutId: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(payoutId)) {
      newExpanded.delete(payoutId);
    } else {
      newExpanded.add(payoutId);
    }
    setExpandedRows(newExpanded);
  };

  const getTransactionCount = (payoutId: number) => {
    if (isLoadingTransactionDetails) return 'Loading...';
    if (!payoutTransactionDetails || !payoutTransactionDetails[payoutId]) return '0';
    
    const payout = payouts.find(p => p.id === payoutId);
    if (!payout) return '0';
    
    const filteredCount = payoutTransactionDetails[payoutId].filter((transaction: any) => {
      const recipientPayoutAmount = transaction.payout_orders
        .filter((po: any) => po.recipient_type === payout.recipient_type && po.recipient_id === payout.recipient_id)
        .reduce((sum: number, po: any) => sum + po.amount, 0);
      return Math.abs(recipientPayoutAmount) > 0.001;
    }).length;
    
    return filteredCount.toString();
  };

  return (
    <div className="space-y-0">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px]"></TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Payout ID</TableHead>
            <TableHead>Recipient</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead className="text-center">Transactions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {payouts.map((payout) => (
            <React.Fragment key={payout.id}>
              <TableRow 
                className="cursor-pointer hover:bg-muted/50" 
                onClick={() => toggleRow(payout.id)}
              >
                <TableCell>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <ChevronRight 
                      className={`h-4 w-4 transition-transform ${
                        expandedRows.has(payout.id) ? 'rotate-90' : ''
                      }`} 
                    />
                  </Button>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    {formatDate(payout.paid_at || payout.created_at)}
                  </div>
                </TableCell>
                <TableCell>
                  <span className="font-mono text-sm font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">
                    #{payout.id}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center space-x-2">
                    {renderEntityIcon(payout.recipient_type)}
                    <span className="font-medium">{payout.recipient_name}</span>
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <span className="font-medium text-green-600">
                    {formatCurrency(payout.amount)}
                  </span>
                </TableCell>
                <TableCell className="text-center">
                  <span className="text-sm text-muted-foreground">
                    {getTransactionCount(payout.id)}
                  </span>
                </TableCell>
              </TableRow>
              
              {expandedRows.has(payout.id) && (
                <TableRow>
                  <TableCell colSpan={6} className="p-0 border-0">
                    <div className="bg-muted/30 px-4 py-6 space-y-6">
                      {/* Bank Details */}
                      {payout.bank_account && (
                        <div className="bg-white rounded-lg p-4 border">
                          <h4 className="font-medium mb-3 flex items-center">
                            <CreditCard className="h-4 w-4 mr-2" />
                            Bank Account Details
                          </h4>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            {payout.bank_name && (
                              <div>
                                <span className="font-medium text-muted-foreground">Bank Name:</span>
                                <div className="mt-1">{payout.bank_name}</div>
                              </div>
                            )}
                                                         <div>
                               <span className="font-medium text-muted-foreground">Account:</span>
                               <div className="mt-1 font-mono">
                                 {payout.bank_account}
                               </div>
                             </div>
                            {payout.payment_reference && (
                              <div className="col-span-2">
                                <span className="font-medium text-muted-foreground">Payment Reference:</span>
                                <div className="mt-1">{payout.payment_reference}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Transaction Details */}
                      <div className="bg-white rounded-lg p-4 border">
                        <h4 className="font-medium mb-4">Included Transactions</h4>
                        {isLoadingTransactionDetails ? (
                          <div className="text-center py-4">
                            <Loader2 className="h-4 w-4 mx-auto animate-spin text-muted-foreground" />
                            <p className="mt-2 text-sm text-muted-foreground">Loading transaction details...</p>
                          </div>
                        ) : payoutTransactionDetails && payoutTransactionDetails[payout.id] ? (
                          <div className="space-y-3">
                            {payoutTransactionDetails[payout.id]
                              .filter((transaction: any) => {
                                const recipientPayoutAmount = transaction.payout_orders
                                  .filter((po: any) => po.recipient_type === payout.recipient_type && po.recipient_id === payout.recipient_id)
                                  .reduce((sum: number, po: any) => sum + po.amount, 0);
                                return Math.abs(recipientPayoutAmount) > 0.001;
                              })
                              .map((transaction: any) => (
                                <div key={transaction.order_id} className="border rounded-lg p-3 bg-gray-50">
                                  <div className="flex items-center justify-between mb-3">
                                    <Link 
                                      to={`/backoffice/orders/${transaction.order_id}`} 
                                      className="font-medium text-blue-600 hover:underline"
                                    >
                                      Order #{transaction.order_id}
                                    </Link>
                                    <div className="flex items-center space-x-2">
                                      {/* Show refund indicators */}
                                      {transaction.payout_orders
                                        .filter((po: any) => po.recipient_type === payout.recipient_type && po.recipient_id === payout.recipient_id)
                                        .some((po: any) => po.amount < 0) && (
                                        <span className="text-xs px-2 py-1 bg-orange-100 text-orange-800 rounded">
                                          Contains refunds
                                        </span>
                                      )}
                                      <span className={`font-medium ${
                                        transaction.payout_orders
                                          .filter((po: any) => po.recipient_type === payout.recipient_type && po.recipient_id === payout.recipient_id)
                                          .reduce((sum: number, po: any) => sum + po.amount, 0) < 0 
                                          ? 'text-red-600' : 'text-green-600'
                                      }`}>
                                        {formatCurrency(transaction.payout_orders
                                          .filter((po: any) => po.recipient_type === payout.recipient_type && po.recipient_id === payout.recipient_id)
                                          .reduce((sum: number, po: any) => sum + po.amount, 0))}
                                      </span>
                                    </div>
                                  </div>
                                  
                                  {/* Transaction Items */}
                                  <div className="space-y-2">
                                    {transaction.payout_orders
                                      .filter((po: any) => po.recipient_type === payout.recipient_type && po.recipient_id === payout.recipient_id)
                                      .map((po: any, idx: number) => (
                                        <div key={idx} className="flex justify-between items-center text-sm">
                                          <span className="text-muted-foreground">
                                            {po.order_item?.label || po.label || po.recipient_type.replace('_', ' ') + ' fee'}
                                            {po.amount < 0 && (
                                              <span className="ml-2 text-xs bg-red-100 text-red-800 px-1 py-0.5 rounded">
                                                Refund
                                              </span>
                                            )}
                                          </span>
                                          <span className={`font-medium ${po.amount < 0 ? 'text-red-600' : ''}`}>
                                            {formatCurrency(po.amount)}
                                          </span>
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground text-center py-4">No transactions found for this payout</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const Payouts = () => {
  const { userRoles, selectedRole, loading, permissionsLoaded, user } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('transactions');
  
  // Pagination state for transactions
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Filter state for transactions
  const [approvalStatusFilter, setApprovalStatusFilter] = useState<string>('all');

  // Approval state
  const [selectedTransactions, setSelectedTransactions] = useState<Set<string>>(new Set());
  const [isUpdatingApproval, setIsUpdatingApproval] = useState(false);

  // Bulk CSV upload state
  const [isProcessingBulkUpload, setIsProcessingBulkUpload] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get API filters based on selected role
  const getApiFilters = () => {
    const filters: any = {};
    
    if (userRoles.platform.length > 0 && selectedRole?.type === 'platform') {
      filters.show_all = true;
    } else if (selectedRole?.type === 'management_company' && selectedRole.entityId) {
      filters.management_company_id = selectedRole.entityId;
    } else {
      filters.show_none = true;
    }
    
    return filters;
  };

  // Query for getting all payout orders (paginated for transactions view)
  const {
    data: payoutOrdersResponse,
    isLoading: isLoadingPayouts
  } = useQuery({
    queryKey: ['payout-orders', selectedRole?.type, selectedRole?.entityId, currentPage, pageSize],
    queryFn: async () => {
      const filters = getApiFilters();
      const paginationParams = {
        limit: pageSize,
        page: currentPage,
        // Don't filter by status - show all payout orders
        exclude_paid_payouts: true, // Exclude fully paid payouts
        ...filters
      };
      console.log('=== PAYOUT ORDERS API CALL ===');
      console.log('API parameters:', paginationParams);
      console.log('Selected role:', selectedRole);
      console.log('Current page:', currentPage);
      console.log('Page size:', pageSize);
      const response = await payoutApi.getPayoutOrders(paginationParams);
      
      // Handle both paginated and non-paginated responses
      if ('data' in response && 'metadata' in response) {
        console.log('Fetched paginated payout orders:', response);
        console.log('=== PAYOUT ORDERS RESPONSE DEBUG ===');
        console.log('Total orders returned:', response.data?.length || 0);
        console.log('Metadata:', response.metadata);
        if (response.data && response.data.length > 0) {
          console.log('First 5 orders:', response.data.slice(0, 5).map(o => ({ order_id: o.order_id, amount: o.amount, recipient_type: o.recipient_type })));
          console.log('Last 5 orders:', response.data.slice(-5).map(o => ({ order_id: o.order_id, amount: o.amount, recipient_type: o.recipient_type })));
          const order2674 = response.data.find(o => o.order_id === 2674);
          if (order2674) {
            console.log('Found order 2674:', order2674);
          } else {
                      console.log('Order 2674 NOT found in response');
          }
        }
        

        
        return response as PaginatedPayoutOrdersResponse;
      } else {
        // Non-paginated response (legacy)
        console.log('Fetched non-paginated payout orders:', response);
        

        
        return {
          data: response as PayoutOrder[],
          metadata: {
            total_count: (response as PayoutOrder[]).length,
            limit: pageSize,
            offset: (currentPage - 1) * pageSize,
            page: currentPage,
            totalPages: 1
          }
        } as PaginatedPayoutOrdersResponse;
      }
    },
    enabled: permissionsLoaded && !!selectedRole
  });

  // Extract paginated payout orders data
  const paginatedPayoutOrders = useMemo(() => {
    if (!payoutOrdersResponse) return [];
    
    if ('data' in payoutOrdersResponse && 'metadata' in payoutOrdersResponse) {
      return payoutOrdersResponse.data || [];
    } else {
      return payoutOrdersResponse as PayoutOrder[];
    }
  }, [payoutOrdersResponse]);

  // Get pagination metadata from the response
  const paginationMetadata = useMemo(() => {
    if (!payoutOrdersResponse) return null;
    
    if ('data' in payoutOrdersResponse && 'metadata' in payoutOrdersResponse) {
      return payoutOrdersResponse.metadata;
    } else {
      // For non-paginated responses, calculate metadata
      const totalCount = (payoutOrdersResponse as PayoutOrder[]).length;
      const totalPages = Math.ceil(totalCount / pageSize);
      return {
        total_count: totalCount,
        page: currentPage,
        limit: pageSize,
        totalPages,
        offset: (currentPage - 1) * pageSize
      };
    }
  }, [payoutOrdersResponse, currentPage, pageSize]);

  // Query for getting associations data (for name lookups) - using same approach as Orders.tsx
  const {
    data: associationsResponse
  } = useQuery({
    queryKey: ['associations-v2', selectedRole?.type, selectedRole?.entityId],
    queryFn: async () => {
      const filters = getApiFilters();
      console.log('=== FETCHING ALL ASSOCIATIONS ===');
      console.log('Filters for associations:', filters);
      console.log('Selected role for associations:', selectedRole);
      
      // Use the same approach as Orders.tsx - let the API handle pagination
      // Don't pass filters to associations API as it handles role-based filtering internally
      const result = await associationApi.list({ 
        queryKey: ["all_associations", { limit: 10000 }]
      });
      
      console.log('=== ASSOCIATIONS FETCH RESULT ===');
      console.log('Associations API response:', result);
      
      return result;
    },
    enabled: permissionsLoaded && !!selectedRole
  });

  // Extract associations data using the same approach as Orders.tsx
  const associations = extractDataArray(associationsResponse);
  


  // Query for getting management companies data (for name lookups)
  const {
    data: managementCompaniesResponse
  } = useQuery({
    queryKey: ['management-companies', selectedRole?.type, selectedRole?.entityId],
    queryFn: async () => {
      const filters = getApiFilters();
      console.log('=== FETCHING ALL MANAGEMENT COMPANIES ===');
      console.log('Filters for management companies:', filters);
      
      // Use the same approach as Orders.tsx - simple API call with high limit
      // Don't pass filters to management companies API as it handles role-based filtering internally
      const result = await managementCompanyApi.list({
        queryKey: ["all_management_companies", { limit: 10000 }]
      });
      
      console.log('=== MANAGEMENT COMPANIES FETCH RESULT ===');
      console.log('Management companies API response:', result);
      
      return result;
    },
    enabled: permissionsLoaded && !!selectedRole
  });

  // Extract management companies data using the same approach as Orders.tsx
  const managementCompanies = extractDataArray(managementCompaniesResponse);

  // Query for getting ALL payout orders (for CSV export - with pagination to ensure we get everything)
  const {
    data: allPayoutOrders,
    isLoading: isLoadingAllPayouts,
    refetch: refetchAllPayouts
  } = useQuery({
    queryKey: ['all-payout-orders-csv-v3', selectedRole?.type, selectedRole?.entityId, associations?.length, managementCompanies?.length], // Added associations and managementCompanies to query key
    queryFn: async () => {
      const filters = getApiFilters();
      console.log('=== CSV EXPORT: Fetching ALL payout orders (V2 WITH PAGINATION) ===');
      console.log('Filters for CSV export:', filters);
      console.log('Selected role:', selectedRole);
      console.log('User roles:', userRoles);
      
      // For CSV export, we want ALL orders including paid ones
      const csvFilters = {
        // Remove status filter to get ALL orders (including paid ones)
        // Remove exclude_paid_payouts to include paid orders
        ...filters
      };
      
      console.log('CSV filters with status filter:', csvFilters);
      console.log('=== STARTING CSV PAGINATION LOOP ===');
      
      // Fetch all data using pagination to ensure we get everything
      let allOrders: PayoutOrder[] = [];
      let page = 1;
      const pageSize = 200; // Use larger page size for efficiency
      let hasMoreData = true;
      
      while (hasMoreData) {
        console.log(`CSV Export: Fetching page ${page} with ${pageSize} records...`);
        
        const ordersResponse = await payoutApi.getPayoutOrders({
          // Remove status and exclude_paid_payouts to get ALL orders including paid ones
          limit: pageSize,
          page: page,
          ...filters
        });
        
        // Handle both paginated and non-paginated responses
        let orders: PayoutOrder[] = [];
        let totalCount = 0;
        
        if ('data' in ordersResponse && 'metadata' in ordersResponse) {
          // Paginated response
          orders = ordersResponse.data || [];
          totalCount = ordersResponse.metadata.total_count;
          console.log(`CSV Export Page ${page}: Got ${orders.length} orders, total count: ${totalCount}`);
        } else {
          // Non-paginated response (legacy)
          orders = ordersResponse as PayoutOrder[];
          console.log(`CSV Export Page ${page}: Got ${orders.length} orders (non-paginated)`);
        }
        
        if (orders.length === 0) {
          hasMoreData = false;
        } else {
          allOrders = allOrders.concat(orders);
          
          // Check if we've reached the end
          if (orders.length < pageSize || allOrders.length >= totalCount) {
            hasMoreData = false;
          } else {
            page++;
          }
        }
      }
      
      console.log('=== CSV EXPORT RESULT ===');
      console.log('Total records received:', allOrders.length);
      console.log('First 3 records:', allOrders.slice(0, 3));
      console.log('Last 3 records:', allOrders.slice(-3));
      

      
      // Count by status
      const statusCounts = allOrders.reduce((acc, order) => {
        acc[order.status] = (acc[order.status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      console.log('Records by status:', statusCounts);
      
      // Count by recipient type
      const recipientCounts = allOrders.reduce((acc, order) => {
        acc[order.recipient_type] = (acc[order.recipient_type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      console.log('Records by recipient type:', recipientCounts);
      
      // Count unique orders and payments
      const uniqueOrders = new Set(allOrders.map(r => r.order_id));
      const uniquePayments = new Set(allOrders.map(r => r.payment_id || `order_${r.order_id}`));
      console.log('Unique orders:', uniqueOrders.size);
      console.log('Unique payments:', uniquePayments.size);
      
      return allOrders;
    },
    enabled: permissionsLoaded && !!selectedRole && !!associations && !!managementCompanies // Added dependencies on associations and managementCompanies
  });

  // Query for getting paid payouts
  const {
    data: paidPayouts,
    isLoading: isLoadingPaidPayouts,
    refetch: refetchPaidPayouts
  } = useQuery({
    queryKey: ['paid-payouts', selectedRole?.type, selectedRole?.entityId],
    queryFn: async () => {
      const filters = getApiFilters();
      const response = await payoutApi.getPayouts('paid', filters);
      
      // Handle both paginated and non-paginated responses
      if (response && typeof response === 'object' && 'data' in response && Array.isArray(response.data)) {
        return response.data; // Return the data array from paginated response
      } else if (Array.isArray(response)) {
        return response; // Return non-paginated response directly
      } else {
        return []; // Fallback to empty array
      }
    },
    enabled: permissionsLoaded && !!selectedRole
  });

  // Query for getting all payouts (for CSV export - to get payout dates)
  const {
    data: allPayouts,
    isLoading: isLoadingAllPayoutsForCSV
  } = useQuery({
    queryKey: ['all-payouts-csv', selectedRole?.type, selectedRole?.entityId],
    queryFn: async () => {
      const filters = getApiFilters();
      console.log('=== CSV EXPORT: Fetching all payouts for payout dates ===');
      console.log('Filters for payouts:', filters);
      
      // Get all payouts (not just paid ones) to get payout dates
      const response = await payoutApi.getPayouts(undefined, {
        ...filters,
        limit: 10000 // Get all payouts
      });
      
      // Handle both paginated and non-paginated responses
      if (response && typeof response === 'object' && 'data' in response && Array.isArray(response.data)) {
        return response.data; // Return the data array from paginated response
      } else if (Array.isArray(response)) {
        return response; // Return non-paginated response directly
      } else {
        return []; // Fallback to empty array
      }
    },
    enabled: permissionsLoaded && !!selectedRole
  });

  // Query for getting detailed transaction information for paid payouts
  const {
    data: payoutTransactionDetails,
    isLoading: isLoadingTransactionDetails,
    refetch: refetchTransactionDetails
  } = useQuery({
    queryKey: ['payout-transaction-details', selectedRole?.type, selectedRole?.entityId, paidPayouts?.map(p => p.id).sort().join(',') || 'no-payouts'],
    queryFn: async () => {
      if (!paidPayouts || paidPayouts.length === 0) return {};
      
      const details: { [payoutId: number]: any[] } = {};
      
      for (const payout of paidPayouts) {
        // Get payout orders for this payout
        const payoutOrdersResponse = await payoutApi.getPayoutOrders({
          payout_id: payout.id,
          ...getApiFilters()
        });
        
        // Handle both paginated and non-paginated responses
        const payoutOrders = Array.isArray(payoutOrdersResponse) 
          ? payoutOrdersResponse 
          : payoutOrdersResponse.data || [];
        
        // Group by order_id and get additional details
        const orderGroups: { [orderId: number]: any } = {};
        
        for (const po of payoutOrders) {
          if (!orderGroups[po.order_id]) {
            orderGroups[po.order_id] = {
              order_id: po.order_id,
              order_date: po.order?.created_at || po.created_at,
              total_payout_amount: 0,
              payout_orders: [],
              payments: [],
              refunds: []
            };
          }
          
          orderGroups[po.order_id].total_payout_amount += po.amount;
          orderGroups[po.order_id].payout_orders.push(po);
        }
        
        // Get payments and refunds for each order
        for (const orderId of Object.keys(orderGroups)) {
          try {
            const orderIdNum = parseInt(orderId);
            
            // Get payments - check if any payout orders have payment_id
            const paymentIds = new Set(
              orderGroups[orderIdNum].payout_orders
                .filter((po: any) => po.payment_id)
                .map((po: any) => po.payment_id)
            );
            
            if (paymentIds.size > 0) {
              // Simulate payment data based on payment_id references
              orderGroups[orderIdNum].payments = Array.from(paymentIds).map(paymentId => ({
                id: paymentId,
                payment_method: 'credit_card', // Would come from actual API
                status: 'paid',
                amount: orderGroups[orderIdNum].total_payout_amount // Approximation
              }));
            } else {
              // Fallback for payout orders without payment_id (legacy data)
              orderGroups[orderIdNum].payments = [{
                id: `order_${orderIdNum}`,
                payment_method: 'unknown',
                status: 'paid',
                amount: orderGroups[orderIdNum].total_payout_amount,
                note: 'Legacy payout without payment reference'
              }];
            }
            
            // Initialize refunds array (would be populated by actual API call)
            orderGroups[orderIdNum].refunds = [];
            
            // Add order total for reference
            orderGroups[orderIdNum].order_total = orderGroups[orderIdNum].total_payout_amount;
            
          } catch (error) {
            console.warn(`Could not fetch payment/refund details for order ${orderId}`);
            orderGroups[parseInt(orderId)].payments = [];
            orderGroups[parseInt(orderId)].refunds = [];
          }
        }
        
        details[payout.id] = Object.values(orderGroups);
      }
      
      return details;
    },
    enabled: permissionsLoaded && !!selectedRole && !!paidPayouts
  });



  // Group payout orders by payment_id for the transactions tab (using paginated data)
  const groupedTransactions = useMemo(() => {
    if (!paginatedPayoutOrders || paginatedPayoutOrders.length === 0) return [];
    const paymentMap = new Map();
    paginatedPayoutOrders.forEach(payoutOrder => {
      const paymentId = payoutOrder.payment_id || `order_${payoutOrder.order_id}`;
      if (!paymentMap.has(paymentId)) {
        paymentMap.set(paymentId, {
          payment_id: paymentId,
          order_id: payoutOrder.order_id,
          date: payoutOrder.order?.created_at ? new Date(payoutOrder.order.created_at).toLocaleDateString() : new Date(payoutOrder.created_at).toLocaleDateString(),
          date_sort: payoutOrder.order?.created_at ? new Date(payoutOrder.order.created_at) : new Date(payoutOrder.created_at),
          total_amount: 0,
          platform_amount: 0,
          management_company_amount: 0,
          association_amount: 0,
          statuses: new Set(),
          has_refunds: false,
          payout_orders: [], // Store individual payout orders for approval actions
          approval_statuses: new Set() // Track approval statuses
        });
      }
      const group = paymentMap.get(paymentId);

      // Add the payout amount to the total amount
      group.total_amount += payoutOrder.amount;

      // Check for refunds (negative amounts)
      if (payoutOrder.amount < 0) {
        group.has_refunds = true;
      }

      // Add to the status collection
      group.statuses.add(payoutOrder.status);

      // Add to approval statuses collection
      if (payoutOrder.approval_status) {
        group.approval_statuses.add(payoutOrder.approval_status);
      }

      // Store the payout order for approval actions
      group.payout_orders.push(payoutOrder);

      // Add to the correct recipient type amount
      if (payoutOrder.recipient_type === 'platform') {
        group.platform_amount += payoutOrder.amount;
      } else if (payoutOrder.recipient_type === 'management_company') {
        group.management_company_amount += payoutOrder.amount;
      } else if (payoutOrder.recipient_type === 'association') {
        group.association_amount += payoutOrder.amount;
      }
    });
    
    // Clean up status display - if we have both paid and ready_to_be_paid statuses,
    // show as "partially paid" for clarity
    paymentMap.forEach((group, paymentId) => {
      if (group.statuses.has('paid') && group.statuses.has('ready_to_be_paid')) {
        // Clear existing statuses and set to partially paid
        group.statuses.clear();
        group.statuses.add('partially_paid');
      }
    });
    
    // Sort by date in descending order (newest first)
    let sortedTransactions = Array.from(paymentMap.values()).sort((a, b) => b.date_sort.getTime() - a.date_sort.getTime());
    
    // Apply approval status filter
    if (approvalStatusFilter !== 'all') {
      sortedTransactions = sortedTransactions.filter(transaction => {
        const approvalStatuses = Array.from(transaction.approval_statuses || new Set());
        return approvalStatuses.includes(approvalStatusFilter);
      });
    }
    
    return sortedTransactions;
  }, [paginatedPayoutOrders, approvalStatusFilter]);

  // Query for getting payout orders that are ready to be paid (with pagination to get all data)
  const {
    data: readyToBePaidOrders,
    isLoading: isLoadingToBePaid,
    refetch: refetchToBePaid
  } = useQuery({
    queryKey: ['payout-orders-ready-to-be-paid-v4', selectedRole?.type, selectedRole?.entityId, associations?.length, managementCompanies?.length], // Updated to v4 to reflect broader status filtering
    queryFn: async () => {
      console.log('=== FETCHING TO BE PAID ORDERS (V4 WITH BROADER STATUS FILTERING) ===');
      console.log('Selected Role:', selectedRole);
      console.log('User Roles:', userRoles);
      console.log('Permissions Loaded:', permissionsLoaded);
      console.log('Associations loaded:', associations?.length || 0);
      console.log('Management Companies loaded:', managementCompanies?.length || 0);
      const filters = getApiFilters();
      console.log('API filters:', filters);
      console.log('=== STARTING PAGINATION LOOP ===');
      
      // Fetch all data using pagination to ensure we get everything
      let allOrders: PayoutOrder[] = [];
      let page = 1;
      const pageSize = 200; // Use larger page size for efficiency
      let hasMoreData = true;
      
      while (hasMoreData) {
        console.log(`Fetching page ${page} with ${pageSize} records...`);
        
        // Fetch orders that are either ready_to_be_paid or pending (approved orders)
        // We'll filter for approved ones on the client side
        // Don't exclude paid payouts here - we want to include orders that are part of pending payouts
        const ordersResponse = await payoutApi.getPayoutOrders({
          // Remove status filter to get both 'ready_to_be_paid' and 'pending' orders
          // Remove exclude_paid_payouts to include orders that are part of pending payouts
          limit: pageSize,
          page: page,
          ...filters
        });
        
        // Handle both paginated and non-paginated responses
        let orders: PayoutOrder[] = [];
        let totalCount = 0;
        
        if ('data' in ordersResponse && 'metadata' in ordersResponse) {
          // Paginated response
          orders = ordersResponse.data || [];
          totalCount = ordersResponse.metadata.total_count;
          console.log(`Page ${page}: Got ${orders.length} orders, total count: ${totalCount}`);
        } else {
          // Non-paginated response (legacy)
          orders = ordersResponse as PayoutOrder[];
          console.log(`Page ${page}: Got ${orders.length} orders (non-paginated)`);
        }
        
        if (orders.length === 0) {
          hasMoreData = false;
        } else {
          allOrders = allOrders.concat(orders);
          
          // Check if we've reached the end
          if (orders.length < pageSize || allOrders.length >= totalCount) {
            hasMoreData = false;
          } else {
            page++;
          }
        }
      }
      
      console.log('=== TO BE PAID ORDERS RESPONSE ===');
      console.log('Total orders fetched:', allOrders.length);
      console.log('Orders with payout_id:', allOrders.filter(o => o.payout_id).length);
      console.log('Orders without payout_id:', allOrders.filter(o => !o.payout_id).length);
      console.log('Orders by status:', allOrders.reduce((acc, o) => {
        acc[o.status] = (acc[o.status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>));
      
      // Log orders by amount (positive vs negative)
      const positiveOrders = allOrders.filter(o => o.amount > 0);
      const negativeOrders = allOrders.filter(o => o.amount < 0);
      const zeroOrders = allOrders.filter(o => o.amount === 0);
      console.log('Orders by amount:', {
        positive: positiveOrders.length,
        negative: negativeOrders.length,
        zero: zeroOrders.length,
        totalPositiveAmount: positiveOrders.reduce((sum, o) => sum + o.amount, 0),
        totalNegativeAmount: negativeOrders.reduce((sum, o) => sum + o.amount, 0)
      });
      
      // Show sample orders
      console.log('Sample orders (first 5):', allOrders.slice(0, 5).map(o => ({
        id: o.id,
        status: o.status,
        amount: o.amount,
        order_id: o.order_id,
        payout_id: o.payout_id,
        recipient_type: o.recipient_type,
        recipient_id: o.recipient_id
      })));
      

      


      // Filter orders to only include approved ones that are ready_to_be_paid
      // These are orders that have been approved and are ready to be paid
      // Also exclude orders that are part of paid payouts
      const approvedOrders = allOrders.filter(order => 
        order.approval_status === 'approved' && 
        order.status === 'ready_to_be_paid' &&
        // Exclude orders that are part of paid payouts
        (!order.payout_id || order.payout?.status !== 'paid')
      );
      
      console.log('=== APPROVAL FILTERING DEBUG ===');
      console.log('Total orders:', allOrders.length);
      console.log('Approved orders:', approvedOrders.length);
      console.log('Orders by approval status:', allOrders.reduce((acc, o) => {
        acc[o.approval_status || 'pending'] = (acc[o.approval_status || 'pending'] || 0) + 1;
        return acc;
      }, {} as Record<string, number>));
      
      // Show orders that are approved but have different statuses
      const approvedButDifferentStatus = allOrders.filter(o => 
        o.approval_status === 'approved' && 
        o.status !== 'ready_to_be_paid' && 
        o.status !== 'pending'
      );
      console.log('Approved orders with other statuses:', approvedButDifferentStatus.map(o => ({
        id: o.id,
        status: o.status,
        approval_status: o.approval_status,
        amount: o.amount,
        order_id: o.order_id
      })));
      
      // Show orders that are part of paid payouts
      const ordersInPaidPayouts = allOrders.filter(o => 
        o.payout_id && o.payout?.status === 'paid'
      );
      console.log('Orders in paid payouts:', ordersInPaidPayouts.map(o => ({
        id: o.id,
        status: o.status,
        approval_status: o.approval_status,
        payout_id: o.payout_id,
        payout_status: o.payout?.status,
        amount: o.amount,
        order_id: o.order_id
      })));
      
      // Show orders that are part of pending payouts
      const ordersInPendingPayouts = allOrders.filter(o => 
        o.payout_id && o.payout?.status === 'pending'
      );
      console.log('Orders in pending payouts:', ordersInPendingPayouts.map(o => ({
        id: o.id,
        status: o.status,
        approval_status: o.approval_status,
        payout_id: o.payout_id,
        payout_status: o.payout?.status,
        amount: o.amount,
        order_id: o.order_id
      })));
      
      // Log approved orders by amount
      const approvedPositiveOrders = approvedOrders.filter(o => o.amount > 0);
      const approvedNegativeOrders = approvedOrders.filter(o => o.amount < 0);
      const approvedZeroOrders = approvedOrders.filter(o => o.amount === 0);
      console.log('Approved orders by amount:', {
        positive: approvedPositiveOrders.length,
        negative: approvedNegativeOrders.length,
        zero: approvedZeroOrders.length,
        totalPositiveAmount: approvedPositiveOrders.reduce((sum, o) => sum + o.amount, 0),
        totalNegativeAmount: approvedNegativeOrders.reduce((sum, o) => sum + o.amount, 0)
      });

      // Group by recipient_type and recipient_id, separating positive and negative amounts
      interface GroupedPayouts {
        [key: string]: {
          recipientType: RecipientType;
          recipientId: number | null;
          recipientName: string;
          amount: number;
          orders: PayoutOrder[];
          positiveAmount: number;
          negativeAmount: number;
        };
      }
      const grouped = approvedOrders.reduce<GroupedPayouts>((acc, order) => {
        const key = `${order.recipient_type}-${order.recipient_id || 'null'}`;
        if (!acc[key]) {
          // Determine the display name based on entity type
          let displayName = "Unknown";
          if (order.recipient_type === 'platform') {
            displayName = "Platform";
          } else if (order.recipient_type === 'management_company' && order.recipient_id) {
            // Try to get name from loaded management companies data, fallback to ID
            const managementCompany = managementCompanies?.find(mc => mc.id === order.recipient_id);
            displayName = managementCompany?.name || `Management Company #${order.recipient_id}`;
          } else if (order.recipient_type === 'association' && order.recipient_id) {
            // Try to get name from loaded associations data, fallback to ID
            const association = associations?.find(a => a.id === order.recipient_id);
            displayName = association?.name || `Association #${order.recipient_id}`;
          }
          acc[key] = {
            recipientType: order.recipient_type as RecipientType,
            recipientId: order.recipient_id,
            recipientName: displayName,
            amount: 0,
            orders: [],
            positiveAmount: 0,
            negativeAmount: 0
          };
        }
        
        // Track positive and negative amounts separately
        if (order.amount > 0) {
          acc[key].positiveAmount += order.amount;
        } else if (order.amount < 0) {
          acc[key].negativeAmount += order.amount;
        }
        
        acc[key].amount += order.amount;
        acc[key].orders.push(order);
        return acc;
      }, {});
      
      // Create netted entries combining payouts and refunds for each recipient
      const nettedGroups: Array<{
        recipientType: RecipientType;
        recipientId: number | null;
        recipientName: string;
        amount: number;
        orders: PayoutOrder[];
        positiveAmount: number;
        negativeAmount: number;
      }> = [];
      
      Object.values(grouped).forEach(group => {
        const netAmount = group.positiveAmount + group.negativeAmount; // negativeAmount is already negative
        
        console.log('=== GROUPING DEBUG ===');
        console.log('Group:', {
          recipientType: group.recipientType,
          recipientId: group.recipientId,
          recipientName: group.recipientName,
          positiveAmount: group.positiveAmount,
          negativeAmount: group.negativeAmount,
          netAmount: netAmount,
          ordersCount: group.orders.length,
          orderIds: [...new Set(group.orders.map(o => o.order_id))].slice(0, 5) // Show first 5 order IDs
        });
        
        // Log individual orders for debugging
        console.log('Orders in group:', group.orders.map(o => ({
          id: o.id,
          order_id: o.order_id,
          amount: o.amount,
          status: o.status,
          approval_status: o.approval_status,
          recipient_type: o.recipient_type,
          recipient_id: o.recipient_id
        })));
        
        // Include groups with any orders, even if net amount is zero
        // This ensures we show refunds even when they exactly match payouts
        if (group.orders.length > 0) {
          console.log('Adding group to netted groups');
          nettedGroups.push({
            recipientType: group.recipientType,
            recipientId: group.recipientId,
            recipientName: group.recipientName,
            amount: netAmount,
            orders: group.orders, // Include all orders (both positive and negative)
            positiveAmount: group.positiveAmount,
            negativeAmount: group.negativeAmount
          });
        } else {
          console.log('Filtering out group due to no orders');
        }
      });
      
      const filteredGroups = nettedGroups;
      
      console.log('=== FINAL RESULT DEBUG ===');
      console.log('Final filtered groups count:', filteredGroups.length);
      console.log('Final groups:', filteredGroups.map(g => ({
        recipientType: g.recipientType,
        recipientId: g.recipientId,
        recipientName: g.recipientName,
        amount: g.amount,
        ordersCount: g.orders.length,
        orderIds: [...new Set(g.orders.map(o => o.order_id))].slice(0, 5) // Show first 5 order IDs
      })));
      

      
      return filteredGroups;
    },
    enabled: permissionsLoaded && !!selectedRole && !!associations && !!managementCompanies // Added dependencies on associations and managementCompanies
  });
  
  // Helper function to get bank details for a recipient
  const getBankDetails = (recipientType: RecipientType, recipientId: number | null) => {
    if (recipientType === 'association' && recipientId) {
      const association = associations?.find(a => a.id === recipientId);
      if (association && association.bank_account) {
        return {
          bankAccount: association.bank_account,
          routingNumber: association.routing,
          bankName: null,
          accountType: null,
          wireInstructions: null
        };
      }
    } else if (recipientType === 'management_company' && recipientId) {
      const managementCompany = managementCompanies?.find(mc => mc.id === recipientId);
      if (managementCompany && managementCompany.account_number) {
        return {
          bankAccount: managementCompany.account_number,
          routingNumber: managementCompany.routing_number,
          bankName: managementCompany.bank_name,
          accountType: managementCompany.account_type,
          wireInstructions: managementCompany.wire_instructions
        };
      }
    }
    return null;
  };

  const handleMarkAsPaid = async (group: {
    recipientType: RecipientType;
    recipientId: number | null;
    recipientName: string;
    amount: number;
    orders: PayoutOrder[];
    positiveAmount?: number;
    negativeAmount?: number;
  }) => {
    try {
      console.log('=== RELEASE FUNDS PROCESS STARTED ===');
      console.log('Group to mark as paid:', group);
      console.log('Orders in group:', group.orders.map(o => ({
        id: o.id,
        status: o.status,
        amount: o.amount,
        order_id: o.order_id,
        payout_id: o.payout_id
      })));
      
      // Get recipient bank info if applicable
      const bankDetails = getBankDetails(group.recipientType, group.recipientId);
      
      // Send all approved orders that are either 'pending' or 'ready_to_be_paid' status
      // The backend will handle the final filtering and grouping
      const readyToPayOrderIds = group.orders
        .filter(order => 
          order.approval_status === 'approved' && 
          (order.status === 'ready_to_be_paid' || order.status === 'pending')
        )
        .map(order => order.id);
      
      console.log('Ready to pay order IDs:', readyToPayOrderIds);
      console.log('Orders by status and approval:', group.orders.map(o => ({
        id: o.id,
        status: o.status,
        approval_status: o.approval_status,
        amount: o.amount,
        order_id: o.order_id
      })));
      console.log('Orders excluded from payout:', group.orders.filter(order => 
        !(order.approval_status === 'approved' && 
          (order.status === 'ready_to_be_paid' || order.status === 'pending'))
      ));
      
      if (readyToPayOrderIds.length === 0) {
        console.log('No eligible orders found - all orders:', group.orders);
        toast({
          title: "No eligible orders",
          description: "No pending orders ready for approval found.",
          variant: "destructive"
        });
        return;
      }
      
      console.log('=== STEP 1: GROUPING PAYOUTS ===');
      console.log('Sending payout order IDs for grouping:', readyToPayOrderIds);
      
      // Group payouts first
      const response = await payoutApi.groupPayoutOrders({
        recipient_type: group.recipientType,
        recipient_id: group.recipientId,
        payout_order_ids: readyToPayOrderIds
      });
      console.log('=== GROUPING RESPONSE ===', response);

      console.log('=== STEP 2: MARKING AS PAID ===');
      // Then update the grouped payout to paid status
      const paymentReference = `Manual payment on ${new Date().toISOString()}`;
      const updateResponse = await payoutApi.updatePayoutStatus(response.id, 'paid', paymentReference);
      console.log('=== UPDATE RESPONSE ===', updateResponse);
      
      console.log('=== RELEASE FUNDS PROCESS COMPLETED ===');
      
      // Determine the appropriate success message based on the transaction type
      let successTitle = "Payment processed";
      let successDescription = "";
      
      if (group.amount > 0) {
        if (group.negativeAmount && group.negativeAmount < 0) {
          successTitle = "Net payment processed";
          successDescription = `Successfully processed net payment of ${formatCurrency(group.amount)} for ${group.recipientName.replace(/ \(.*\)/, '')} (${formatCurrency(group.positiveAmount || 0)} payout minus ${formatCurrency(Math.abs(group.negativeAmount || 0))} recovery)`;
        } else {
          successTitle = "Payment released";
          successDescription = `Successfully released ${formatCurrency(group.amount)} to ${group.recipientName.replace(/ \(.*\)/, '')}`;
        }
      } else if (group.amount < 0) {
        successTitle = "Funds recovered";
        successDescription = `Successfully processed recovery of ${formatCurrency(Math.abs(group.amount))} from ${group.recipientName.replace(/ \(.*\)/, '')}`;
      } else {
        // Net amount is zero (payouts and refunds cancel each other out)
        successTitle = "Net zero transaction processed";
        successDescription = `Successfully processed net zero transaction for ${group.recipientName.replace(/ \(.*\)/, '')} (${formatCurrency(group.positiveAmount || 0)} payout offset by ${formatCurrency(Math.abs(group.negativeAmount || 0))} refund)`;
      }
      
      toast({
        title: successTitle,
        description: successDescription
      });

      // Immediately refresh the data for both tabs
      console.log('=== REFRESHING DATA ===');
      console.log('Triggering data refresh...');
      refetchToBePaid();
      // Refresh paid payouts first, then transaction details will auto-refresh due to query key dependency
      await refetchPaidPayouts();
      console.log('Data refresh completed');
    } catch (error) {
      console.error('=== ERROR IN RELEASE FUNDS PROCESS ===', error);
      toast({
        title: "Error",
        description: "Failed to mark payment as paid. Please try again.",
        variant: "destructive"
      });
    }
  };
  
  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  const handleApprovalStatusFilterChange = (value: string) => {
    setApprovalStatusFilter(value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Approval functions
  const handleSelectTransaction = (paymentId: string) => {
    const newSelected = new Set(selectedTransactions);
    if (newSelected.has(paymentId)) {
      newSelected.delete(paymentId);
    } else {
      newSelected.add(paymentId);
    }
    setSelectedTransactions(newSelected);
  };

  const handleSelectAllTransactions = () => {
    if (selectedTransactions.size === groupedTransactions.length) {
      setSelectedTransactions(new Set());
    } else {
      setSelectedTransactions(new Set(groupedTransactions.map(t => t.payment_id.toString())));
    }
  };



  const handleUpdateApprovalStatus = async (approvalStatus: 'approved' | 'rejected') => {
    if (selectedTransactions.size === 0) {
      toast({
        title: "No transactions selected",
        description: "Please select at least one transaction to update.",
        variant: "destructive",
      });
      return;
    }

    setIsUpdatingApproval(true);
    try {
      // Get all payout order IDs from selected transactions
      const payoutOrderIds: number[] = [];
      groupedTransactions.forEach(transaction => {
        if (selectedTransactions.has(transaction.payment_id.toString())) {
          transaction.payout_orders.forEach(payoutOrder => {
            payoutOrderIds.push(payoutOrder.id);
          });
        }
      });

      if (payoutOrderIds.length === 0) {
        toast({
          title: "No payout orders found",
          description: "No payout orders found in selected transactions.",
          variant: "destructive",
        });
        return;
      }

      await payoutApi.updateApprovalStatus(payoutOrderIds, approvalStatus, user?.id ? Number(user.id) : undefined);
      
      toast({
        title: "Approval status updated",
        description: `Updated ${payoutOrderIds.length} payout orders to ${approvalStatus}.`,
      });

      // Clear selection and refetch data
      setSelectedTransactions(new Set());
      refetchAllPayouts();
    } catch (error) {
      console.error('Error updating approval status:', error);
      toast({
        title: "Error updating approval status",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setIsUpdatingApproval(false);
    }
  };

  // Bulk CSV upload function
  const handleBulkApprovalUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('Bulk upload function called!', event);
    const file = event.target.files?.[0];
    console.log('Selected file:', file);
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      toast({
        title: "Invalid file type",
        description: "Please upload a CSV file.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessingBulkUpload(true);
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        toast({
          title: "Invalid CSV file",
          description: "CSV file must have at least a header row and one data row.",
          variant: "destructive",
        });
        return;
      }

      // Parse CSV
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const paymentIdIndex = headers.findIndex(h => h === 'payment_id');
      const approvalStatusIndex = headers.findIndex(h => h === 'approval_status');

      if (paymentIdIndex === -1 || approvalStatusIndex === -1) {
        toast({
          title: "Invalid CSV format",
          description: "CSV must have 'payment_id' and 'approval_status' columns.",
          variant: "destructive",
        });
        return;
      }

      // Process data rows
      const updates: Array<{ paymentId: string; approvalStatus: string }> = [];
      const errors: string[] = [];

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i];
        const values = line.split(',').map(v => v.trim());
        
        if (values.length < Math.max(paymentIdIndex, approvalStatusIndex) + 1) {
          errors.push(`Row ${i + 1}: Insufficient columns`);
          continue;
        }

        const paymentId = values[paymentIdIndex];
        const approvalStatus = values[approvalStatusIndex].toLowerCase();

        if (!paymentId) {
          errors.push(`Row ${i + 1}: Missing payment_id`);
          continue;
        }

        if (!['approved', 'rejected', 'pending'].includes(approvalStatus)) {
          errors.push(`Row ${i + 1}: Invalid approval_status '${approvalStatus}'. Must be 'approved', 'rejected', or 'pending'`);
          continue;
        }

        updates.push({ paymentId, approvalStatus });
      }

      if (errors.length > 0) {
        toast({
          title: "CSV validation errors",
          description: `Found ${errors.length} errors. Please fix and try again.`,
          variant: "destructive",
        });
        console.error('CSV validation errors:', errors);
        return;
      }

      if (updates.length === 0) {
        toast({
          title: "No valid updates",
          description: "No valid rows found in CSV file.",
          variant: "destructive",
        });
        return;
      }

      // Group updates by approval status
      const approvedUpdates = updates.filter(u => u.approvalStatus === 'approved');
      const rejectedUpdates = updates.filter(u => u.approvalStatus === 'rejected');
      const pendingUpdates = updates.filter(u => u.approvalStatus === 'pending');

      let totalUpdated = 0;

      // Process approved updates
      if (approvedUpdates.length > 0) {
        const approvedPaymentIds = approvedUpdates.map(u => u.paymentId);
        const approvedOrderIds = await getPayoutOrderIdsFromPaymentIds(approvedPaymentIds);
        if (approvedOrderIds.length > 0) {
          await payoutApi.updateApprovalStatus(approvedOrderIds, 'approved', user?.id ? Number(user.id) : undefined);
          totalUpdated += approvedOrderIds.length;
        }
      }

      // Process rejected updates
      if (rejectedUpdates.length > 0) {
        const rejectedPaymentIds = rejectedUpdates.map(u => u.paymentId);
        const rejectedOrderIds = await getPayoutOrderIdsFromPaymentIds(rejectedPaymentIds);
        if (rejectedOrderIds.length > 0) {
          await payoutApi.updateApprovalStatus(rejectedOrderIds, 'rejected', user?.id ? Number(user.id) : undefined);
          totalUpdated += rejectedOrderIds.length;
        }
      }

      // Process pending updates (reset to pending)
      if (pendingUpdates.length > 0) {
        const pendingPaymentIds = pendingUpdates.map(u => u.paymentId);
        const pendingOrderIds = await getPayoutOrderIdsFromPaymentIds(pendingPaymentIds);
        if (pendingOrderIds.length > 0) {
          await payoutApi.updateApprovalStatus(pendingOrderIds, 'pending', user?.id ? Number(user.id) : undefined);
          totalUpdated += pendingOrderIds.length;
        }
      }

      toast({
        title: "Bulk approval update completed",
        description: `Successfully updated ${totalUpdated} payout orders from ${updates.length} CSV rows. The UI will refresh to show the new approval statuses.`,
      });

      // Clear file input and refetch data
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Refetch both the main payout orders data and the CSV export data
      refetchAllPayouts();
      
      // Invalidate and refetch the main payout orders query to update the UI
      queryClient.invalidateQueries({
        queryKey: ['payout-orders']
      });
      
      // Also invalidate any queries that might depend on approval status
      queryClient.invalidateQueries({
        queryKey: ['all-payout-orders-csv']
      });
    } catch (error) {
      console.error('Error processing bulk approval upload:', error);
      toast({
        title: "Error processing bulk upload",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setIsProcessingBulkUpload(false);
    }
  };

  // Helper function to get payout order IDs from payment IDs
  const getPayoutOrderIdsFromPaymentIds = async (paymentIds: string[]): Promise<number[]> => {
    const payoutOrderIds: number[] = [];
    
    // Get all payout orders to find matches
    const allPayoutOrdersData = await payoutApi.getPayoutOrders({
      limit: 10000, // Get all orders
      ...getApiFilters()
    });

    const orders = Array.isArray(allPayoutOrdersData) 
      ? allPayoutOrdersData 
      : allPayoutOrdersData.data || [];

    // Find payout orders that match the payment IDs
    for (const paymentId of paymentIds) {
      const matchingOrders = orders.filter(order => {
        const orderPaymentId = order.payment_id || `order_${order.order_id}`;
        return orderPaymentId.toString() === paymentId;
      });
      
      matchingOrders.forEach(order => {
        payoutOrderIds.push(order.id);
      });
    }

    return payoutOrderIds;
  };

  // Helper function to render entity icons
  const renderEntityIcon = (entityType: string) => {
    switch (entityType) {
      case 'management_company':
        return <Building className="h-5 w-5 mr-2" />;
      case 'association':
        return <Home className="h-5 w-5 mr-2" />;
      case 'platform':
        return <Server className="h-5 w-5 mr-2" />;
      default:
        return null;
    }
  };

  // Create grouped transactions for CSV export (using all data)
  const allGroupedTransactions = useMemo(() => {
    if (!allPayoutOrders || allPayoutOrders.length === 0) return [];
    const paymentMap = new Map();
    allPayoutOrders.forEach(payoutOrder => {
      const paymentId = payoutOrder.payment_id || `order_${payoutOrder.order_id}`;
      if (!paymentMap.has(paymentId)) {
        paymentMap.set(paymentId, {
          payment_id: paymentId,
          order_id: payoutOrder.order_id,
          date: payoutOrder.order?.created_at ? new Date(payoutOrder.order.created_at).toLocaleDateString() : new Date(payoutOrder.created_at).toLocaleDateString(),
          date_sort: payoutOrder.order?.created_at ? new Date(payoutOrder.order.created_at) : new Date(payoutOrder.created_at),
          total_amount: 0,
          platform_amount: 0,
          management_company_amount: 0,
          association_amount: 0,
          statuses: new Set(),
          has_refunds: false
        });
      }
      const group = paymentMap.get(paymentId);

      // Add the payout amount to the total amount
      group.total_amount += payoutOrder.amount;

      // Check for refunds (negative amounts)
      if (payoutOrder.amount < 0) {
        group.has_refunds = true;
      }

      // Add to the status collection
      group.statuses.add(payoutOrder.status);

      // Add to the correct recipient type amount
      if (payoutOrder.recipient_type === 'platform') {
        group.platform_amount += payoutOrder.amount;
      } else if (payoutOrder.recipient_type === 'management_company') {
        group.management_company_amount += payoutOrder.amount;
      } else if (payoutOrder.recipient_type === 'association') {
        group.association_amount += payoutOrder.amount;
      }
    });
    
    // Clean up status display
    paymentMap.forEach((group, paymentId) => {
      if (group.statuses.has('paid') && group.statuses.has('ready_to_be_paid')) {
        group.statuses.clear();
        group.statuses.add('partially_paid');
      }
    });
    
    // Sort by date in descending order (newest first)
    return Array.from(paymentMap.values()).sort((a, b) => b.date_sort.getTime() - a.date_sort.getTime());
  }, [allPayoutOrders]);

  // Function to export transactions to CSV
  const exportTransactionsCSV = () => {
    console.log('=== CSV EXPORT DEBUGGING ===');
    console.log('allPayoutOrders:', allPayoutOrders?.length, 'records');
    console.log('allPayouts:', allPayouts?.length, 'records');
    console.log('isLoadingAllPayoutsForCSV:', isLoadingAllPayoutsForCSV);
    
    if (!allPayoutOrders || allPayoutOrders.length === 0) {
      toast({
        title: "No data to export",
        description: "There are no payout orders available to export.",
        variant: "destructive"
      });
      return;
    }



    // Log details about the data being exported
    console.log('Raw payout orders sample:', allPayoutOrders.slice(0, 5));
    
    // Log payout statistics
    const ordersWithPayouts = allPayoutOrders.filter(o => o.payout_id);
    const ordersWithPayoutDates = allPayoutOrders.filter(o => {
      if (!o.payout_id || !allPayouts) return false;
      const payout = allPayouts.find(p => p.id === o.payout_id);
      return payout && payout.paid_at;
    });
    console.log('=== CSV EXPORT PAYOUT STATISTICS ===');
    console.log('Total orders:', allPayoutOrders.length);
    console.log('Orders with payout_id:', ordersWithPayouts.length);
    console.log('Orders with payout date (from payouts table):', ordersWithPayoutDates.length);
    console.log('Orders by status:', allPayoutOrders.reduce((acc, o) => {
      acc[o.status] = (acc[o.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>));
    
    // Show sample orders with payouts
    if (ordersWithPayouts.length > 0) {
      console.log('Sample orders with payouts:', ordersWithPayouts.slice(0, 3).map(o => {
        const payout = allPayouts?.find(p => p.id === o.payout_id);
        return {
          id: o.id,
          order_id: o.order_id,
          payout_id: o.payout_id,
          payout_paid_at: payout?.paid_at || 'Not found',
          amount: o.amount,
          status: o.status
        };
      }));
    }
    
    // Show sample orders with payouts
    if (ordersWithPayouts.length > 0) {
      console.log('Sample orders with payouts:', ordersWithPayouts.slice(0, 3).map(o => {
        const payout = allPayouts?.find(p => p.id === o.payout_id);
        return {
          id: o.id,
          order_id: o.order_id,
          payout_id: o.payout_id,
          payout_paid_at: payout?.paid_at || 'Not found',
          amount: o.amount,
          status: o.status
        };
      }));
    }
    
    // Sort payout orders by date, order_id, and payment_id
    const sortedPayoutOrders = [...allPayoutOrders].sort((a, b) => {
      // First sort by date (newest first)
      const dateA = new Date(a.order?.created_at || a.created_at);
      const dateB = new Date(b.order?.created_at || b.created_at);
      if (dateA.getTime() !== dateB.getTime()) {
        return dateB.getTime() - dateA.getTime(); // Newest first
      }
      
      // Then by order_id (ascending)
      if (a.order_id !== b.order_id) {
        return a.order_id - b.order_id;
      }
      
      // Finally by payment_id (ascending)
      const paymentIdA = String(a.payment_id || `order_${a.order_id}`);
      const paymentIdB = String(b.payment_id || `order_${b.order_id}`);
      return paymentIdA.localeCompare(paymentIdB);
    });
    
    console.log('Sorted payout orders sample:', sortedPayoutOrders.slice(0, 5));
    
    // Create CSV header with Customer Email, Approval Status, and Payout information columns
    const header = ['Date', 'Payment ID', 'Order ID', 'Customer Email', 'Amount', 'Recipient Type', 'Recipient ID', 'Recipient Name', 'Status', 'Approval Status', 'Payout Date'].join(',');

    // Create CSV rows from sorted payout orders
    const rows = sortedPayoutOrders.map(payoutOrder => {
      // Format date in America/New_York timezone (EDT/EST) without timezone display
      const rawDate = payoutOrder.order?.created_at || payoutOrder.created_at;
      const formattedDate = DateTime.fromISO(rawDate, { zone: 'utc' })
        .setZone('America/New_York')
        .toFormat('yyyy-MM-dd HH:mm');
      
      // Format approval status for display
      const approvalStatus = payoutOrder.approval_status || 'pending';
      const approvalStatusDisplay = approvalStatus === 'approved' ? 'Approved' : 
                                   approvalStatus === 'rejected' ? 'Rejected' : 
                                   'Pending';
      
      // Format payout date if available
      let payoutDate = '';
      if (payoutOrder.payout_id && allPayouts) {
        // Find the payout record to get the paid_at date
        const payout = allPayouts.find(p => p.id === payoutOrder.payout_id);
        if (payout && payout.paid_at) {
          payoutDate = DateTime.fromISO(payout.paid_at, { zone: 'utc' })
            .setZone('America/New_York')
            .toFormat('yyyy-MM-dd HH:mm');
        }
      }
      
      return [
        formattedDate,
        payoutOrder.payment_id || `order_${payoutOrder.order_id}`,
        payoutOrder.order_id,
        `"${(payoutOrder.order?.customer?.email || '').replace(/"/g, '""')}"`, // Add customer email with proper escaping
        payoutOrder.amount.toFixed(2),
        payoutOrder.recipient_type,
        payoutOrder.recipient_id || '',
        `"${(payoutOrder.recipient_name || '').replace(/"/g, '""')}"`, // Escape quotes and wrap in quotes
        payoutOrder.status,
        approvalStatusDisplay,
        payoutDate
      ].join(',');
    });

    // Combine header and rows
    const csvContent = [header, ...rows].join('\n');

    console.log('CSV content length:', csvContent.length);
    console.log('CSV rows generated:', rows.length);

    // Create a blob and download the file
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `payout-orders-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "Export successful",
      description: `Exported ${allPayoutOrders.length} payout orders to CSV with payout information.`
    });
  };

  // Function to format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Function to display status with proper formatting
  const displayStatus = (status: string) => {
    if (status === 'paid') return 'Paid';
    if (status === 'ready_to_be_paid') return 'Ready to be paid';
    if (status === 'partially_paid') return 'Partially paid';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  // Show loading state while permissions are loading
  if (loading || !permissionsLoaded) {
    return (
      <BackofficeLayout>
        <div className="container mx-auto py-8">
          <div className="flex justify-center items-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
            <span className="ml-2">Loading permissions...</span>
          </div>
        </div>
      </BackofficeLayout>
    );
  }

  // Show message if no role is selected
  if (!selectedRole) {
    return (
      <BackofficeLayout>
        <div className="container mx-auto py-8">
          <div className="text-center py-8">
            <h3 className="text-lg font-medium">No role selected</h3>
            <p className="text-sm text-muted-foreground">
              Please select a role to view payouts.
            </p>
          </div>
        </div>
      </BackofficeLayout>
    );
  }

  return <BackofficeLayout>
      <div className="container mx-auto py-8">
        <h1 className="text-3xl font-bold mb-6">Payouts Management</h1>
        
        <Tabs defaultValue="transactions" onValueChange={handleTabChange}>
          <TabsList className="mb-6">
            <TabsTrigger value="transactions">Payment Transactions</TabsTrigger>
            <TabsTrigger value="to-be-paid">To Be Paid</TabsTrigger>
            <TabsTrigger value="paid">Paid</TabsTrigger>
          </TabsList>
          
          <TabsContent value="transactions" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Payment Transactions</CardTitle>
                  <CardDescription>
                    View all payment transactions regardless of approval status
                    {paginationMetadata && (
                      <span className="block text-sm text-muted-foreground mt-1">
                        Showing {((paginationMetadata.page - 1) * paginationMetadata.limit) + 1} to {Math.min(paginationMetadata.page * paginationMetadata.limit, paginationMetadata.total_count)} of {paginationMetadata.total_count} payment transactions
                      </span>
                    )}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  {selectedTransactions.size > 0 && (
                    <>
                      <Button 
                        variant="outline" 
                        onClick={() => handleUpdateApprovalStatus('approved')}
                        disabled={isUpdatingApproval}
                        className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                      >
                        {isUpdatingApproval ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Check className="h-4 w-4 mr-2" />
                        )}
                        Approve ({selectedTransactions.size})
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => handleUpdateApprovalStatus('rejected')}
                        disabled={isUpdatingApproval}
                        className="bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
                      >
                        {isUpdatingApproval ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <X className="h-4 w-4 mr-2" />
                        )}
                        Reject ({selectedTransactions.size})
                      </Button>
                    </>
                  )}
                  <Button variant="outline" onClick={exportTransactionsCSV} disabled={isLoadingAllPayouts || isLoadingAllPayoutsForCSV || !allPayoutOrders || allPayoutOrders.length === 0}>
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                    {(isLoadingAllPayouts || isLoadingAllPayoutsForCSV) ? (
                      <Loader2 className="h-4 w-4 ml-2 animate-spin" />
                    ) : null}
                  </Button>
                  <Button 
                    variant="outline" 
                    disabled={isProcessingBulkUpload}
                    onClick={() => {
                      console.log('Button clicked!');
                      fileInputRef.current?.click();
                    }}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Bulk Approval Upload
                    {isProcessingBulkUpload ? (
                      <Loader2 className="h-4 w-4 ml-2 animate-spin" />
                    ) : null}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleBulkApprovalUpload}
                    className="hidden"
                    disabled={isProcessingBulkUpload}
                  />
                </div>
              </CardHeader>
              
              {/* Filter Section */}
              <div className="px-6 py-4 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <label htmlFor="approval-status-filter" className="text-sm font-medium">
                        Approval Status:
                      </label>
                      <Select value={approvalStatusFilter} onValueChange={handleApprovalStatusFilterChange}>
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="All statuses" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All statuses</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="approved">Approved</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <details className="cursor-pointer">
                      <summary className="hover:text-foreground">📋 CSV Upload Format</summary>
                      <div className="mt-2 p-3 bg-muted rounded-md text-xs">
                        <p className="font-medium mb-2">CSV must have these columns:</p>
                        <ul className="space-y-1">
                          <li><code>payment_id</code> - The payment ID from the table</li>
                          <li><code>approval_status</code> - One of: "approved", "rejected", "pending"</li>
                        </ul>
                        <p className="mt-2 font-medium">Example:</p>
                        <pre className="bg-background p-2 rounded text-xs">
payment_id,approval_status<br/>
12345,approved<br/>
67890,rejected<br/>
11111,pending
                        </pre>
                      </div>
                    </details>
                  </div>
                </div>
              </div>
              
              <CardContent>
                {isLoadingPayouts ? <div className="text-center py-4">
                    <Loader2 className="h-5 w-5 mx-auto animate-spin text-muted-foreground" />
                    <p className="mt-2 text-sm text-muted-foreground">Loading transactions...</p>
                  </div> :                                         <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[50px]">
                            <input
                              type="checkbox"
                              checked={selectedTransactions.size === groupedTransactions.length && groupedTransactions.length > 0}
                              onChange={handleSelectAllTransactions}
                              className="rounded border-gray-300"
                            />
                          </TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Payment ID</TableHead>
                          <TableHead>Order ID</TableHead>
                          <TableHead>Net Amount</TableHead>
                          <TableHead>Platform Amount</TableHead>
                          <TableHead>Management Company Amount</TableHead>
                          <TableHead>Association Amount</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Approval</TableHead>
                        </TableRow>
                      </TableHeader>
                    <TableBody>
                      {groupedTransactions.length > 0 ? groupedTransactions.map(transaction => <TableRow key={transaction.payment_id}>
                            <TableCell>
                              <input
                                type="checkbox"
                                checked={selectedTransactions.has(transaction.payment_id.toString())}
                                onChange={() => handleSelectTransaction(transaction.payment_id.toString())}
                                className="rounded border-gray-300"
                              />
                            </TableCell>
                            <TableCell>{transaction.date}</TableCell>
                            <TableCell>
                              <span className="font-mono text-sm">
                                {typeof transaction.payment_id === 'number' ? `#${transaction.payment_id}` : transaction.payment_id}
                              </span>
                              {transaction.has_refunds && (
                                <span className="ml-2 text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                                  Refunds
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Link to={`/backoffice/orders/${transaction.order_id}`} className="text-blue-600 hover:underline">
                                #{transaction.order_id}
                              </Link>
                            </TableCell>
                            <TableCell>
                              <span className={transaction.total_amount < 0 ? 'text-red-600 font-medium' : ''}>
                                {formatCurrency(transaction.total_amount)}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className={transaction.platform_amount < 0 ? 'text-red-600' : ''}>
                                {formatCurrency(transaction.platform_amount)}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className={transaction.management_company_amount < 0 ? 'text-red-600' : ''}>
                                {formatCurrency(transaction.management_company_amount)}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className={transaction.association_amount < 0 ? 'text-red-600' : ''}>
                                {formatCurrency(transaction.association_amount)}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {Array.from(transaction.statuses).map((status, idx) => <span key={idx} className={
                                    status === 'paid' || status === 'completed' ? 'text-green-600 font-medium' :
                                    status === 'partially_paid' ? 'text-blue-600 font-medium' :
                                    'text-amber-600 font-medium'
                                  }>
                                    {displayStatus(status.toString())}{idx < transaction.statuses.size - 1 ? ', ' : ''}
                                  </span>)}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {Array.from(transaction.approval_statuses || new Set()).map((status, idx) => (
                                  <span key={idx} className={
                                    status === 'approved' ? 'text-green-600 font-medium' :
                                    status === 'rejected' ? 'text-red-600 font-medium' :
                                    'text-amber-600 font-medium'
                                  }>
                                    {status === 'approved' ? 'Approved' : 
                                     status === 'rejected' ? 'Rejected' : 
                                     'Pending'}
                                    {idx < (transaction.approval_statuses?.size || 0) - 1 ? ', ' : ''}
                                  </span>
                                ))}
                                {(!transaction.approval_statuses || transaction.approval_statuses.size === 0) && (
                                  <span className="text-gray-500">Pending</span>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>) : <TableRow>
                          <TableCell colSpan={10} className="text-center py-4">
                            {isLoadingPayouts ? "Loading transactions..." : "No transactions found"}
                          </TableCell>
                        </TableRow>}
                    </TableBody>
                  </Table>}
                  
                  {/* Pagination Controls */}
                  {paginationMetadata && paginationMetadata.totalPages > 1 && (
                    <div className="flex items-center justify-between px-2 py-4 border-t">
                      <div className="flex-1 text-sm text-muted-foreground">
                        Showing {((paginationMetadata.page - 1) * paginationMetadata.limit) + 1} to {Math.min(paginationMetadata.page * paginationMetadata.limit, paginationMetadata.total_count)} of {paginationMetadata.total_count} payment transactions
                      </div>
                      <div className="flex items-center space-x-6 lg:space-x-8">
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-medium">Rows per page</p>
                          <Select
                            value={pageSize.toString()}
                            onValueChange={(value) => {
                              setPageSize(Number(value));
                              setCurrentPage(1); // Reset to first page when changing page size
                            }}
                          >
                            <SelectTrigger className="h-8 w-[70px]">
                              <SelectValue placeholder={pageSize} />
                            </SelectTrigger>
                            <SelectContent side="top">
                              {[25, 50, 100, 200].map((size) => (
                                <SelectItem key={size} value={size.toString()}>
                                  {size}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex w-[100px] items-center justify-center text-sm font-medium">
                          Page {paginationMetadata.page} of {paginationMetadata.totalPages}
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            className="hidden h-8 w-8 p-0 lg:flex"
                            onClick={() => setCurrentPage(1)}
                            disabled={paginationMetadata.page === 1}
                          >
                            <span className="sr-only">Go to first page</span>
                            <ChevronLeft className="h-4 w-4" />
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            className="h-8 w-8 p-0"
                            onClick={() => setCurrentPage(paginationMetadata.page - 1)}
                            disabled={paginationMetadata.page === 1}
                          >
                            <span className="sr-only">Go to previous page</span>
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            className="h-8 w-8 p-0"
                            onClick={() => setCurrentPage(paginationMetadata.page + 1)}
                            disabled={paginationMetadata.page === paginationMetadata.totalPages}
                          >
                            <span className="sr-only">Go to next page</span>
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            className="hidden h-8 w-8 p-0 lg:flex"
                            onClick={() => setCurrentPage(paginationMetadata.totalPages)}
                            disabled={paginationMetadata.page === paginationMetadata.totalPages}
                          >
                            <span className="sr-only">Go to last page</span>
                            <ChevronRight className="h-4 w-4" />
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="to-be-paid" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Payouts To Be Paid</CardTitle>
              </CardHeader>
              <CardContent>
                {isLoadingToBePaid ? <div className="text-center py-4">
                    <Loader2 className="h-5 w-5 mx-auto animate-spin text-muted-foreground" />
                    <p className="mt-2 text-sm text-muted-foreground">Loading payouts to be paid...</p>
                  </div> :                     <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Recipient</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Bank Details</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {readyToBePaidOrders && readyToBePaidOrders.length > 0 ? readyToBePaidOrders.map((group: any, index: number) => <TableRow key={`${group.recipientType}-${group.recipientId || 'platform'}-${group.amount >= 0 ? 'payout' : 'refund'}-${index}`}>
                            <TableCell>
                              {group.recipientType === 'management_company' && group.recipientId ? <Link to={`/backoffice/management-companies/${group.recipientId}/edit`} className="hover:underline flex items-center">
                                  {renderEntityIcon(group.recipientType)}
                                  {group.recipientName}
                                </Link> : group.recipientType === 'association' && group.recipientId ? <Link to={`/backoffice/associations/${group.recipientId}`} className=" hover:underline flex items-center">
                                  {renderEntityIcon(group.recipientType)}
                                  {group.recipientName}
                                </Link> : <div className="flex items-center">
                                  {renderEntityIcon(group.recipientType)}
                                  {group.recipientName}
                                </div>}
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                <span className={group.amount < 0 ? 'text-red-600 font-medium' : ''}>
                                  {formatCurrency(group.amount)}
                                </span>
                                {group.positiveAmount > 0 && group.negativeAmount < 0 && (
                                  <div className="text-xs text-muted-foreground">
                                    {formatCurrency(group.positiveAmount)} payout - {formatCurrency(Math.abs(group.negativeAmount))} refund
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const bankDetails = getBankDetails(group.recipientType, group.recipientId);
                                if (!bankDetails) {
                                  return <span className="text-muted-foreground text-sm">No bank details</span>;
                                }
                                return (
                                  <div className="text-sm">
                                    {bankDetails.bankName && (
                                      <div className="font-medium">{bankDetails.bankName}</div>
                                    )}
                                    <div className="space-y-1">
                                      {bankDetails.bankAccount && (
                                        <div className="text-muted-foreground">
                                          <span className="font-medium">Account:</span> {bankDetails.bankAccount}
                                        </div>
                                      )}
                                      {bankDetails.routingNumber && (
                                        <div className="text-muted-foreground">
                                          <span className="font-medium">Routing:</span> {bankDetails.routingNumber}
                                        </div>
                                      )}
                                      {bankDetails.accountType && (
                                        <div className="text-muted-foreground">
                                          <span className="font-medium">Type:</span> {bankDetails.accountType}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              {group.orders.length > 0 && (
                                <Button 
                                  className={`${
                                    group.amount > 0 
                                      ? 'bg-brand-orange text-white hover:bg-orange-600' 
                                      : group.amount < 0 
                                        ? 'bg-red-600 text-white hover:bg-red-700'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                  }`}
                                  onClick={() => handleMarkAsPaid(group)}
                                >
                                  {group.amount > 0 ? 'Release funds' : 
                                   group.amount < 0 ? 'Process refund' : 
                                   'Process net zero'}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>) : <TableRow>
                          <TableCell colSpan={4} className="text-center py-4">
                            {isLoadingToBePaid ? "Loading payouts..." : "No payments to be made"}
                          </TableCell>
                        </TableRow>}
                    </TableBody>
                  </Table>}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="paid" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Paid Payouts</CardTitle>
                <CardDescription>View all completed payouts with transaction details</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingPaidPayouts ? (
                  <div className="text-center py-4">
                    <Loader2 className="h-5 w-5 mx-auto animate-spin text-muted-foreground" />
                    <p className="mt-2 text-sm text-muted-foreground">Loading paid payouts...</p>
                  </div>
                ) : paidPayouts && paidPayouts.length > 0 ? (
                  <PaidPayoutsTable 
                    payouts={paidPayouts}
                    payoutTransactionDetails={payoutTransactionDetails}
                    isLoadingTransactionDetails={isLoadingTransactionDetails}
                    formatDate={formatDate}
                    formatCurrency={formatCurrency}
                    renderEntityIcon={renderEntityIcon}
                  />
                ) : (
                  <div className="text-center py-4">
                    <p className="text-muted-foreground">No paid payouts found</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </BackofficeLayout>;
};

export default Payouts;